const BASE = globalThis.location?.hostname === '127.0.0.1' ?
  'http://127.0.0.1:3000/shopify-apihub/flexpatio' :
  'https://lulu.aries-kmc.top:3000/shopify-apihub/flexpatio'
const AVAILABILITY_ENDPOINT = `${BASE}/zoom/available-times`
const AUTO_SESSION_STARTED_KEY = 'flexpatio:video-appointment:session-started-at'
const AUTO_SESSION_SHOWN_KEY = 'flexpatio:video-appointment:auto-shown'
const AUTO_LAST_SHOWN_KEY = 'flexpatio:video-appointment:last-shown-at'
const HUBSPOT_CHAT_STATE_EVENT = 'ly:hubspot-chat:state-change'
const VIDEO_APPOINTMENT_STATE_EVENT = 'ly:video-appointment:state-change'

export function normalizeAvailability(payload, referenceDate, maxSlots = 7) {
  const availability = payload?.data || payload
  const safeMaximum = Math.min(7, Math.max(1, Number(maxSlots) || 7))
  const todayKey = typeof referenceDate === 'string'
    ? referenceDate.slice(0, 10)
    : referenceDate.toISOString().slice(0, 10)
  const referenceTime = typeof referenceDate === 'string'
    ? new Date(`${todayKey}T00:00:00`).getTime()
    : referenceDate.getTime()
  const available = (Array.isArray(availability?.days) ? availability.days : [])
    .flatMap((day) => (Array.isArray(day?.spots) ? day.spots : []))
    .filter((spot) => (
      spot?.status === 'available'
      && Number(spot.availableNumber) > 0
      && typeof spot.startTime === 'string'
      && Number.isFinite(new Date(spot.startTime).getTime())
      && new Date(spot.startTime).getTime() >= referenceTime
    ))
    .sort((left, right) => new Date(left.startTime) - new Date(right.startTime))

  const todaySlots = available
    .filter((spot) => spot.startTime.slice(0, 10) === todayKey)
    .slice(0, safeMaximum)
  const nextAvailable = available.find((spot) => spot.startTime.slice(0, 10) > todayKey) || null

  return {
    duration: Number(availability?.duration) || 30,
    todaySlots,
    nextAvailable,
  }
}

export function buildAvailabilityRequestUrl(endpoint, timeMin, timeMax, timeZone) {
  const url = new URL(endpoint)
  url.searchParams.set('timeMin', timeMin)
  url.searchParams.set('timeMax', timeMax)
  url.searchParams.set('timeZone', timeZone)
  return url.toString()
}

export function buildBookingUrl(baseUrl, startTime) {
  const url = new URL(baseUrl)
  url.searchParams.set('slot', startTime)
  return url.toString()
}

export function resolveLauncherPlacement(viewportWidth, settings) {
  const mobile = Number(viewportWidth) < 768
  return {
    bottom: Number(mobile ? settings.mobileBottom : settings.desktopBottom) || (mobile ? 88 : 92),
    right: Number(mobile ? settings.mobileRight : settings.desktopRight) || (mobile ? 16 : 24),
  }
}

export function shouldHideAppointmentLauncher(appointmentOpen, liveChatOpen) {
  return Boolean(liveChatOpen)
}

export function resolveOpenStackShift({ viewportHeight, dialogTop, margin }) {
  const height = Number(viewportHeight)
  const top = Number(dialogTop)
  const safeMargin = Number(margin)
  if (!Number.isFinite(height) || height <= 0 || !Number.isFinite(top)
    || !Number.isFinite(safeMargin) || safeMargin < 0) return 0
  return Math.max(0, safeMargin - top)
}

export function resolveAutoPopupSchedule(settings) {
  const delay = (enabled, seconds) => {
    if (enabled !== true && enabled !== 'true') return null
    const value = Number(seconds)
    return Math.round(Math.min(120, Math.max(1, Number.isFinite(value) ? value : 1)) * 1000)
  }

  return {
    openDelayMs: delay(settings.autoOpenEnabled, settings.autoOpenDelay),
    closeDelayMs: delay(settings.autoCloseEnabled, settings.autoCloseDelay),
    cooldownMs: Math.round(
      Math.min(168, Math.max(1, Number(settings.repeatAfterHours) || 24)) * 60 * 60 * 1000,
    ),
  }
}

export function remainingAutoOpenDelay(sessionStartedAt, now, configuredDelayMs) {
  const startedAt = Number(sessionStartedAt)
  const currentTime = Number(now)
  const delay = Math.max(0, Number(configuredDelayMs) || 0)
  if (!Number.isFinite(startedAt) || !Number.isFinite(currentTime)) return delay
  return Math.max(0, delay - Math.max(0, currentTime - startedAt))
}

export function shouldAutoOpenPopup({
  hasTodaySlots,
  sessionShown,
  lastShownAt,
  now,
  cooldownMs,
  appointmentOpen,
  liveChatOpen,
}) {
  const previousShownAt = Number(lastShownAt)
  const currentTime = Number(now)
  const cooldown = Math.max(0, Number(cooldownMs) || 0)
  const shownRecently = Number.isFinite(previousShownAt)
    && previousShownAt > 0
    && Number.isFinite(currentTime)
    && currentTime - previousShownAt < cooldown

  return Boolean(
    hasTodaySlots
    && !sessionShown
    && !shownRecently
    && !appointmentOpen
    && !liveChatOpen
  )
}

export function shouldAutoClose(autoOpened, userInteracted, enabled) {
  return Boolean(autoOpened && !userInteracted && enabled)
}

export function slotLayoutClass(totalSlots, index) {
  const classes = []
  if (index === 0) classes.push('is-featured')
  if (index === 0 || Number(totalSlots) <= 2) classes.push('is-full-width')
  return classes.join(' ')
}

const AppointmentPopupElement = typeof HTMLElement === 'undefined' ? class { } : HTMLElement

export class LyVideoAppointmentPopup extends AppointmentPopupElement {
  connectedCallback() {
    this.launcher = this.querySelector('[data-appointment-launcher]')
    this.overlay = this.querySelector('[data-appointment-overlay]')
    this.dialog = this.querySelector('[data-appointment-dialog]')
    this.closeButton = this.querySelector('[data-appointment-close]')
    this.status = this.querySelector('[data-appointment-status]')
    this.slotList = this.querySelector('[data-appointment-slot-list]')
    this.primaryAction = this.querySelector('[data-appointment-primary]')
    this.loading = this.querySelector('[data-appointment-loading]')
    this.message = this.querySelector('[data-appointment-message]')
    this.handleClick = this.handleClick.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleResize = this.handleResize.bind(this)
    this.handleLiveChatState = this.handleLiveChatState.bind(this)
    this.handlePopupInteraction = this.handlePopupInteraction.bind(this)
    this.liveChatOpen = false
    this.addEventListener('click', this.handleClick)
    document.addEventListener('keydown', this.handleKeydown)
    document.addEventListener(HUBSPOT_CHAT_STATE_EVENT, this.handleLiveChatState)
    window.addEventListener('resize', this.handleResize, { passive: true })
    this.dialog?.addEventListener('pointerenter', this.handlePopupInteraction)
    this.dialog?.addEventListener('focusin', this.handlePopupInteraction)
    this.syncLauncherPlacement()
    this.setupSupportDock()
    this.scheduleAutoOpen()
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick)
    document.removeEventListener('keydown', this.handleKeydown)
    document.removeEventListener(HUBSPOT_CHAT_STATE_EVENT, this.handleLiveChatState)
    window.removeEventListener('resize', this.handleResize)
    this.dialog?.removeEventListener('pointerenter', this.handlePopupInteraction)
    this.dialog?.removeEventListener('focusin', this.handlePopupInteraction)
    this.unregisterSupportDock?.()
    this.unregisterSupportDock = null
    this.clearAutoTimers()
    this.clearOpenStackPlacement()
  }

  setupSupportDock() {
    const dockApi = window.LyFloatingSupportDock
    if (!dockApi?.getSupportDockController || !this.launcher) return
    this.supportDockController = dockApi.getSupportDockController()
    this.unregisterSupportDock = this.supportDockController.register({
      element: this,
      launcher: this.launcher,
      kind: this.dataset.supportDockKind || 'video',
      settings: dockApi.resolveSupportDockSettings(this.dataset),
      syncVisibility: () => this.syncLauncherState(),
    })
  }

  fallbackLauncherPlacement() {
    return resolveLauncherPlacement(window.innerWidth, {
      desktopBottom: this.dataset.desktopBottomOffset,
      desktopRight: this.dataset.desktopRightOffset,
      mobileBottom: this.dataset.mobileBottomOffset,
      mobileRight: this.dataset.mobileRightOffset,
    })
  }

  syncLauncherState(liveChatOpen = this.liveChatOpen) {
    if (!this.launcher) return
    this.launcher.hidden = shouldHideAppointmentLauncher(
      this.classList.contains('is-open'),
      liveChatOpen,
    ) || this.classList.contains('is-support-dock-hidden')
    const open = this.classList.contains('is-open')
    this.launcher.setAttribute('aria-expanded', open ? 'true' : 'false')
    this.launcher.setAttribute(
      'aria-label',
      open ? this.launcher.dataset.closeLabel : this.launcher.dataset.openLabel,
    )
  }

  syncLauncherPlacement() {
    const placement = this.fallbackLauncherPlacement()
    this.setPlacementProperty('--ly-video-launcher-bottom', `${placement.bottom}px`)
    this.setPlacementProperty('--ly-video-launcher-right', `${placement.right}px`)
  }

  handleResize() {
    this.syncLauncherPlacement()
    this.syncOpenStackPlacement()
  }

  clearOpenStackPlacement() {
    this.style?.removeProperty('--ly-support-open-stack-shift-y')
  }

  syncOpenStackPlacement() {
    this.clearOpenStackPlacement()
    if (!this.classList.contains('is-open') || !this.dialog) return
    const shift = resolveOpenStackShift({
      viewportHeight: globalThis.window?.innerHeight,
      dialogTop: this.dialog.getBoundingClientRect().top,
      margin: 16,
    })
    if (shift > 0) this.style?.setProperty('--ly-support-open-stack-shift-y', `${shift}px`)
  }

  handleLiveChatState(event) {
    const open = Boolean(event.detail?.open)
    this.liveChatOpen = open
    this.classList.toggle('is-live-chat-open', open)
    if (open && this.classList.contains('is-open')) {
      this.close({ restoreFocus: false })
    }
    this.syncLauncherState(open)
  }

  emitOpenState(open) {
    document.dispatchEvent(new CustomEvent(VIDEO_APPOINTMENT_STATE_EVENT, {
      detail: { open: Boolean(open) },
    }))
  }

  setPlacementProperty(property, value) {
    if (this.style.getPropertyValue(property) !== value) this.style.setProperty(property, value)
  }

  handleClick(event) {
    const launcher = event.target.closest('[data-appointment-launcher]')
    const close = event.target.closest('[data-appointment-close]')
    const booking = event.target.closest('[data-appointment-start-time]')

    if (this.dialog?.contains(event.target)) this.handlePopupInteraction()
    if (launcher) {
      if (this.supportDockController?.isDockedEntry(this)) {
        this.supportDockController.expand()
        return
      }
      if (this.classList.contains('is-open')) this.close()
      else this.open({ automatic: false })
    }
    if (close) this.close()
    if (booking?.dataset.appointmentStartTime) this.openBooking(booking.dataset.appointmentStartTime)
  }

  handleKeydown(event) {
    if (this.overlay?.hidden) return
    if (event.key === 'Escape') {
      event.preventDefault()
      this.close()
    }
  }

  autoPopupSchedule() {
    return resolveAutoPopupSchedule({
      autoOpenEnabled: this.dataset.autoOpenEnabled,
      autoOpenDelay: this.dataset.autoOpenDelay,
      autoCloseEnabled: this.dataset.autoCloseEnabled,
      autoCloseDelay: this.dataset.autoCloseDelay,
      repeatAfterHours: this.dataset.repeatAfterHours,
    })
  }

  scheduleAutoOpen() {
    const { openDelayMs, cooldownMs } = this.autoPopupSchedule()
    if (openDelayMs === null) return
    const now = Date.now()
    const sessionShown = this.readStorage(window.sessionStorage, AUTO_SESSION_SHOWN_KEY) === 'true'
    const lastShownAt = Number(this.readStorage(window.localStorage, AUTO_LAST_SHOWN_KEY))
    if (sessionShown || (lastShownAt > 0 && now - lastShownAt < cooldownMs)) return

    const storedStartedAt = Number(this.readStorage(window.sessionStorage, AUTO_SESSION_STARTED_KEY))
    const sessionStartedAt = Number.isFinite(storedStartedAt) && storedStartedAt > 0 ? storedStartedAt : now
    if (sessionStartedAt === now) {
      this.writeStorage(window.sessionStorage, AUTO_SESSION_STARTED_KEY, String(sessionStartedAt))
    }
    const remainingDelay = remainingAutoOpenDelay(sessionStartedAt, now, openDelayMs)
    this.ensureAvailability()
    this.autoOpenTimer = window.setTimeout(() => {
      this.autoOpenTimer = null
      this.attemptAutoOpen()
    }, remainingDelay)
  }

  async attemptAutoOpen() {
    const result = await this.ensureAvailability()
    if (!this.isConnected) return
    const { cooldownMs } = this.autoPopupSchedule()
    const sessionShown = this.readStorage(window.sessionStorage, AUTO_SESSION_SHOWN_KEY) === 'true'
    const lastShownAt = this.readStorage(window.localStorage, AUTO_LAST_SHOWN_KEY)
    if (shouldAutoOpenPopup({
      hasTodaySlots: Boolean(result?.todaySlots?.length),
      sessionShown,
      lastShownAt,
      now: Date.now(),
      cooldownMs,
      appointmentOpen: this.classList.contains('is-open'),
      liveChatOpen: this.liveChatOpen,
    })) {
      this.open({ automatic: true })
    }
  }

  scheduleAutoClose() {
    const { closeDelayMs } = this.autoPopupSchedule()
    if (!shouldAutoClose(this.autoOpened, this.userInteracted, closeDelayMs !== null)) return
    this.autoCloseTimer = window.setTimeout(() => {
      this.autoCloseTimer = null
      if (shouldAutoClose(this.autoOpened, this.userInteracted, true)) this.close({ restoreFocus: false })
    }, closeDelayMs)
  }

  handlePopupInteraction() {
    this.userInteracted = true
    if (this.autoCloseTimer) window.clearTimeout(this.autoCloseTimer)
    this.autoCloseTimer = null
  }

  clearAutoTimers() {
    if (this.autoOpenTimer) window.clearTimeout(this.autoOpenTimer)
    if (this.autoCloseTimer) window.clearTimeout(this.autoCloseTimer)
    this.autoOpenTimer = null
    this.autoCloseTimer = null
  }

  readStorage(storage, key) {
    try {
      return storage?.getItem(key) || null
    } catch (error) {
      return null
    }
  }

  writeStorage(storage, key, value) {
    try {
      storage?.setItem(key, value)
    } catch (error) {
      // Storage can be unavailable in strict privacy modes; keep the popup functional.
    }
  }

  recordPopupExposure() {
    this.writeStorage(window.sessionStorage, AUTO_SESSION_SHOWN_KEY, 'true')
    this.writeStorage(window.localStorage, AUTO_LAST_SHOWN_KEY, String(Date.now()))
  }

  open({ automatic = false } = {}) {
    if (!this.overlay || !this.launcher || this.liveChatOpen) return
    if (this.autoOpenTimer) window.clearTimeout(this.autoOpenTimer)
    this.autoOpenTimer = null
    this.autoOpened = automatic
    this.userInteracted = false
    this.recordPopupExposure()
    this.overlay.hidden = false
    this.classList.add('is-open')
    this.syncLauncherState()
    this.syncOpenStackPlacement()
    this.emitOpenState(true)
    if (!automatic) this.closeButton?.focus()
    this.ensureAvailability()
    this.scheduleAutoClose()
  }

  close({ restoreFocus = true } = {}) {
    if (!this.overlay || !this.launcher) return
    const wasOpen = this.classList.contains('is-open')
    if (this.autoCloseTimer) window.clearTimeout(this.autoCloseTimer)
    this.autoCloseTimer = null
    this.overlay.hidden = true
    this.classList.remove('is-open')
    this.syncLauncherState()
    this.clearOpenStackPlacement()
    if (wasOpen) this.emitOpenState(false)
    if (restoreFocus && !this.launcher.hidden) this.launcher.focus()
  }

  ensureAvailability() {
    const cacheAge = Date.now() - (this.loadedAt || 0)
    if (this.latestAvailability && cacheAge <= 60_000) return Promise.resolve(this.latestAvailability)
    if (this.availabilityPromise) return this.availabilityPromise
    this.availabilityPromise = this.loadAvailability().finally(() => {
      this.availabilityPromise = null
    })
    return this.availabilityPromise
  }

  async loadAvailability() {
    const bookingUrl = this.dataset.bookingUrl?.trim()
    if (!bookingUrl) {
      this.renderFallback(this.dataset.unavailableText)
      return null
    }

    this.setLoading(true)
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || this.dataset.fallbackTimeZone
      const now = new Date()
      const date = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0'),
      ].join('-')
      const timeMin = new Date(`${date}T00:00:00.000`).toISOString()
      const timeMax = new Date(`${date}T23:59:59.999`).toISOString()
      const url = buildAvailabilityRequestUrl(
        AVAILABILITY_ENDPOINT,
        timeMin,
        timeMax,
        timeZone,
      )
      const response = await fetch(url, { headers: { Accept: 'application/json' } })
      if (!response.ok) throw new Error(`Availability request failed: ${response.status}`)
      const payload = await response.json()
      if (Number(payload?.code) !== 200) {
        throw new Error(payload?.msg || 'Zoom availability request failed')
      }
      const result = normalizeAvailability(
        payload,
        date,
        this.dataset.maxSlots,
      )
      this.bookingUrl = bookingUrl
      this.renderAvailability(result, timeZone)
      this.latestAvailability = result
      this.loadedAt = Date.now()
      return result
    } catch (error) {
      console.warn('[ly-video-appointment-popup] Unable to load availability', error)
      this.renderFallback(this.dataset.errorText)
      return null
    } finally {
      this.setLoading(false)
    }
  }

  setLoading(isLoading) {
    if (this.loading) this.loading.hidden = !isLoading
    if (!isLoading) return this.syncOpenStackPlacement()
    if (this.status) this.status.hidden = true
    if (this.slotList) this.slotList.hidden = true
    if (this.primaryAction) this.primaryAction.hidden = true
    if (this.message) this.message.hidden = true
    this.syncOpenStackPlacement()
  }

  renderAvailability(result, timeZone) {
    if (!result.todaySlots.length) {
      if (result.nextAvailable) {
        this.renderNextAvailable(result.nextAvailable, timeZone)
      } else {
        this.renderFallback(this.dataset.unavailableText)
      }
      return
    }

    this.status.textContent = this.dataset.availableText
    this.status.hidden = false
    this.message.hidden = true
    this.slotList.replaceChildren(...result.todaySlots.map((slot, index) => (
      this.createSlotButton(slot, timeZone, slotLayoutClass(result.todaySlots.length, index))
    )))
    this.slotList.hidden = false
    this.configurePrimary(result.todaySlots[0], timeZone)
    this.syncOpenStackPlacement()
  }

  renderNextAvailable(slot, timeZone) {
    const date = this.formatDate(slot.startTime, timeZone)
    this.status.textContent = this.dataset.unavailableTodayText
    this.status.hidden = false
    this.message.textContent = `${this.dataset.nextOpeningText} ${date}`
    this.message.hidden = false
    this.slotList.replaceChildren(this.createSlotButton(slot, timeZone, slotLayoutClass(1, 0)))
    this.slotList.hidden = false
    this.configurePrimary(slot, timeZone)
    this.syncOpenStackPlacement()
  }

  renderFallback(copy) {
    if (this.loading) this.loading.hidden = true
    if (this.status) {
      this.status.textContent = this.dataset.availableText
      this.status.hidden = false
    }
    if (this.slotList) {
      this.slotList.replaceChildren()
      this.slotList.hidden = true
    }
    if (this.primaryAction) this.primaryAction.hidden = true
    if (this.message) {
      this.message.textContent = copy || ''
      this.message.hidden = false
    }
    this.syncOpenStackPlacement()
  }

  createSlotButton(slot, timeZone, layoutClass) {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = `ly-video-appointment-popup__slot${layoutClass ? ` ${layoutClass}` : ''}`
    button.dataset.appointmentStartTime = slot.startTime
    button.setAttribute('aria-label', `${this.dataset.bookLabel} ${this.formatTime(slot.startTime, timeZone)}`)

    const time = document.createElement('strong')
    time.textContent = this.formatTime(slot.startTime, timeZone)
    button.append(time)
    if (layoutClass.includes('is-featured')) {
      const label = document.createElement('span')
      label.textContent = this.dataset.nextOpeningText
      button.append(label)
    }
    const arrow = document.createElement('span')
    arrow.className = 'ly-video-appointment-popup__arrow'
    arrow.setAttribute('aria-hidden', 'true')
    arrow.textContent = '›'
    button.append(arrow)
    return button
  }

  configurePrimary(slot, timeZone) {
    if (!this.primaryAction) return
    this.primaryAction.dataset.appointmentStartTime = slot.startTime
    this.primaryAction.textContent = `${this.dataset.bookLabel} ${this.formatTime(slot.startTime, timeZone)}`
    this.primaryAction.hidden = false
  }

  formatTime(startTime, timeZone) {
    return new Intl.DateTimeFormat(document.documentElement.lang || 'en-US', {
      timeZone,
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(startTime))
  }

  formatDate(startTime, timeZone) {
    return new Intl.DateTimeFormat(document.documentElement.lang || 'en-US', {
      timeZone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).format(new Date(startTime))
  }

  openBooking(startTime) {
    const url = buildBookingUrl(this.bookingUrl, startTime)
    if (this.dataset.openNewTab === 'true') {
      window.open(url, '_blank', 'noopener,noreferrer')
    } else {
      window.location.assign(url)
    }
  }
}

if (typeof window !== 'undefined' && !customElements.get('ly-video-appointment-popup')) {
  customElements.define('ly-video-appointment-popup', LyVideoAppointmentPopup)
}
